import React, { useRef, useState } from 'react';
import { X, Printer, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { OrderTicket, type OrderTicketData } from './OrderTicket';
import { Modal } from '../ui';
import { api } from '../../utils/api';

interface Props {
  data: OrderTicketData | null;
  onClose: () => void;
}

export function OrderTicketModal({ data, onClose }: Props) {
  const printRef = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (!data) return null;

  const handlePrint = async () => {
    if (!data?.orderId) {
      setError('No se puede imprimir: falta el ID de la orden.');
      return;
    }

    setPrinting(true);
    setError(null);
    setSuccess(false);

    try {
      // Encolar el trabajo en la BD → el Print Bridge local lo recoge e imprime
      await api.post('/print-jobs/enqueue', {
        order_id: data.orderId,
        type: 'kitchen',
        only_waiting: data.onlyPending,
      });

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
      }, 1800);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Error al encolar la impresión.';
      setError(msg);
    } finally {
      setPrinting(false);
    }
  };


  return (
    <>

      <Modal open={!!data} onClose={onClose} titleId="order-ticket-title" className="max-w-sm">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 id="order-ticket-title" className="text-base font-semibold text-text">Comanda para cocina</h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-muted/50" aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto bg-gray-100 px-2 py-2 flex justify-center">
          <div ref={printRef} id="order-ticket-print" className="shadow-none" style={{ background: 'white', border: '1px solid #ddd' }}>
            <OrderTicket data={data} />
          </div>
        </div>

        {error && (
          <div className="mx-5 mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <AlertCircle size={15} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex gap-3 border-t border-border px-5 py-4">
          <button
            onClick={handlePrint}
            disabled={printing || success}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:opacity-60 ${
              success
                ? 'bg-emerald-500 text-white'
                : 'bg-primary text-white hover:opacity-90'
            }`}
          >
            {printing ? (
              <><Loader2 size={16} className="animate-spin" /> Enviando…</>
            ) : success ? (
              <><CheckCircle2 size={16} /> ¡Enviado al bridge!</>
            ) : (
              <><Printer size={16} /> Imprimir comanda</>
            )}
          </button>
          <button onClick={onClose} className="rounded-xl border border-border px-4 py-2.5 text-sm font-medium text-text-muted hover:bg-muted/30">
            Cerrar
          </button>
        </div>
      </Modal>
    </>
  );
}

export default OrderTicketModal;
